import { Suspense, lazy, useCallback, useEffect, useState } from "react";

/**
 * ★ 開いたときだけ読み込む。
 *
 * CodeMirror は 300 KB 級で、バンドル全体の半分以上を占める。
 * チャット画面や Run 一覧が払う理由は無い。
 */
import { DangerButton } from "../ui/DangerButton.js";
import { Drawer } from "../ui/Drawer.js";
import { BlurFade, NumberTicker, Skeleton } from "../ui/magic.js";

const JsonEditor = lazy(() =>
  import("./JsonEditor.js").then((m) => ({ default: m.JsonEditor })),
);
import {
  RcaApiError,
  type RcaClient,
  type ApiKeyView,
  type AuditEntry,
  type BundleAgent,
  type BundleSkill,
  type BundleView,
  type IssuedApiKey,
  type BundlePatch,
  type CredentialView,
  type EnvironmentDoc,
  type LogEntry,
  type Metrics,
  type ProjectView,
  type RunView,
} from "@rca/sdk";

/**
 * 管理画面（W6 / FR-W2 〜 FR-W5）。
 *
 * データ取得と表示だけを持つ。整形が要るものは純関数に切り出して
 * テストできるようにしている。
 */

// ------------------------------------------------------------ 共通部品

function useAsync<T>(load: () => Promise<T>, deps: readonly unknown[]): {
  data: T | null;
  error: string | null;
  loading: boolean;
  reload: () => void;
} {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    load()
      .then((value) => {
        if (cancelled) return;
        setData(value);
        setError(null);
      })
      .catch((cause: unknown) => {
        if (cancelled) return;
        setError(cause instanceof RcaApiError ? cause.message : String(cause));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce]);

  return { data, error, loading, reload: () => setNonce((n) => n + 1) };
}

function Panel({
  title,
  error,
  loading,
  empty,
  children,
  actions,
}: {
  title: string;
  error: string | null;
  loading: boolean;
  empty?: boolean;
  children: React.ReactNode;
  actions?: React.ReactNode;
}): React.JSX.Element {
  return (
    <section className="stack">
      <header className="row">
        <h2 style={{ margin: 0, fontSize: 16 }}>{title}</h2>
        <span className="spacer" style={{ flex: 1 }} />
        {actions}
      </header>
      {error !== null && (
        <p className="error-banner" role="alert">
          {error}
        </p>
      )}
      {loading ? (
        // ★ 「読み込み中…」の一行より、入るものの形を出す方が待ちやすい。
        <Skeleton rows={4} />
      ) : empty === true ? (
        <p className="empty">データがありません</p>
      ) : (
        children
      )}
    </section>
  );
}

// -------------------------------------------------------------- 整形

/** 期間をミリ秒から読める形に。 */
/**
 * いまいる場所。`projectId` が null ならテナント直下。
 *
 * ★ 一覧・作成・削除はすべてこれを通す。画面ごとに「いまどこか」を
 * 判断させると、片方だけ直すという事故が起きる。
 */
export interface Place {
  readonly projectId: string | null;
}

export const TENANT_PLACE: Place = { projectId: null };

/**
 * どの階層のものかの印。
 *
 * ★ プロジェクトの居場所ではテナントのものが混ざって見える。
 * 印が無いと「消せないものが並んでいる」理由が分からない。
 */
export function OwnerBadge({ projectId }: { projectId: string | null | undefined }): React.JSX.Element {
  return projectId === null || projectId === undefined ? (
    <span className="badge" title="テナントのもの。どのプロジェクトからも見える">
      テナント
    </span>
  ) : (
    <span className="badge accent" title="このプロジェクトのもの">
      プロジェクト
    </span>
  );
}

/** 上位（テナント）のものは、いまの居場所からは編集できない。 */
export function ownedHere(item: { projectId?: string | null }, place: Place): boolean {
  return (item.projectId ?? null) === place.projectId;
}

export function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  const minutes = Math.floor(ms / 60_000);
  return `${minutes}m ${Math.round((ms % 60_000) / 1000)}s`;
}

/** バイト数を読める形に。 */
export function formatBytes(size: number | undefined): string {
  if (size === undefined) return "-";
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / 1024 / 1024).toFixed(1)} MB`;
}

export function formatCost(usd: number): string {
  return `$${usd.toFixed(usd < 1 ? 4 : 2)}`;
}

export function formatTimestamp(iso: string): string {
  // 秒までで十分。タイムゾーンはブラウザに任せる。
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleString();
}

export const STATUS_TONE: Record<string, string> = {
  succeeded: "ok",
  failed: "danger",
  timeout: "danger",
  cancelled: "warn",
  running: "warn",
};

export const LEVEL_TONE: Record<string, string> = {
  error: "danger",
  warn: "warn",
  info: "",
  debug: "",
};

// --------------------------------------------------------------- Runs

export function RunsView({
  client,
  onSelect,
}: {
  client: RcaClient;
  onSelect: (runId: string) => void;
}): React.JSX.Element {
  const [status, setStatus] = useState("");
  const { data, error, loading, reload } = useAsync(
    () => client.runs.list(status === "" ? {} : { status }),
    [status],
  );

  return (
    <Panel
      title="Runs"
      error={error}
      loading={loading}
      empty={data?.items.length === 0}
      actions={
        <button type="button" onClick={reload}>
          再読込
        </button>
      }
    >
      <div className="filters">
        <select aria-label="状態" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">すべての状態</option>
          {["queued", "running", "succeeded", "failed", "cancelled", "timeout"].map((value) => (
            <option key={value} value={value}>
              {value}
            </option>
          ))}
        </select>
      </div>
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>ID</th>
              <th>状態</th>
              <th>プロンプト</th>
              <th className="num">コスト</th>
              <th>作成</th>
            </tr>
          </thead>
          <tbody>
            {(data?.items ?? []).map((run: RunView) => (
              <tr key={run.id}>
                <td>
                  <button type="button" className="mono" onClick={() => onSelect(run.id)}>
                    {run.id}
                  </button>
                </td>
                <td>
                  <span className={`badge ${STATUS_TONE[run.status] ?? ""}`}>{run.status}</span>
                </td>
                {/* ★ **全文を残す。** 以前は 80 字で切っていたので、続きが
                    あることすら分からず、コピーも検索もできなかった。
                    見た目の省略は CSS（clamp）に任せる。
                    ★ セルではなく**内側**に掛ける — 表の td には上限が
                    効かない（実測済み、NOTES 参照）。 */}
                <td>
                  <span className="clamp" title={run.prompt} data-testid="run-prompt">
                    {run.prompt}
                  </span>
                </td>
                <td className="mono num" data-testid="run-cost">
                  {run.usage?.costUsd === undefined ? "-" : formatCost(run.usage.costUsd)}
                </td>
                <td className="mono">{formatTimestamp(run.createdAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Panel>
  );
}

/**
 * 成果物を認証付きで取り、ブラウザに保存させる。
 *
 * API は Bearer を要求するので `<a href>` では落ちる（本番で 401 を確認）。
 */
async function downloadArtifact(client: RcaClient, runId: string, name: string): Promise<void> {
  const response = await client.runs.artifact(runId, name);
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  try {
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = name;
    anchor.click();
  } finally {
    URL.revokeObjectURL(url);
  }
}

export function RunDetailView({
  client,
  runId,
  onBack,
}: {
  client: RcaClient;
  runId: string;
  onBack: () => void;
}): React.JSX.Element {
  const run = useAsync(() => client.runs.get(runId), [runId]);
  const logs = useAsync(() => client.admin.logs({ runId, limit: 200 }), [runId]);
  const artifacts = useAsync(() => client.runs.artifacts(runId), [runId]);

  return (
    <div className="stack">
      <button type="button" onClick={onBack} style={{ alignSelf: "flex-start" }}>
        ← Runs に戻る
      </button>

      <Panel title={`Run ${runId}`} error={run.error} loading={run.loading}>
        {run.data !== null && (
          <div className="stack">
            <div className="row">
              <span className={`badge ${STATUS_TONE[run.data.status] ?? ""}`}>
                {run.data.status}
              </span>
              {run.data.usage !== null && (
                <span className="mono muted">
                  in {run.data.usage.inputTokens ?? 0} / out {run.data.usage.outputTokens ?? 0}
                  {run.data.usage.costUsd !== undefined && ` / ${formatCost(run.data.usage.costUsd)}`}
                </span>
              )}
            </div>
            {run.data.error !== null && (
              <p className="error-banner">
                {run.data.error.code}: {run.data.error.message}
              </p>
            )}
            <details>
              <summary>実効設定と provenance</summary>
              <pre className="mono">{JSON.stringify(run.data.environment, null, 2)}</pre>
            </details>
          </div>
        )}
      </Panel>

      {(artifacts.data?.artifacts.length ?? 0) > 0 && (
        <Panel title="成果物" error={artifacts.error} loading={artifacts.loading}>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>名前</th>
                  <th>種別</th>
                  <th>サイズ</th>
                </tr>
              </thead>
              <tbody>
                {(artifacts.data?.artifacts ?? []).map((artifact) => (
                  <tr key={artifact.name}>
                    <td>
                      {/* ★ 素のリンクでは Authorization が付かず 401 になる。
                          クライアント経由で取ってから保存させる。 */}
                      <button
                        type="button"
                        className="linklike"
                        onClick={() => void downloadArtifact(client, runId, artifact.name)}
                      >
                        {artifact.name}
                      </button>
                      {artifact.truncated === true && (
                        <span className="badge warn" style={{ marginLeft: 8 }}>
                          切り詰め
                        </span>
                      )}
                    </td>
                    <td className="mono">{artifact.contentType ?? "-"}</td>
                    <td className="mono">{formatBytes(artifact.size)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      )}

      <Panel
        title="この Run のログ"
        error={logs.error}
        loading={logs.loading}
        empty={logs.data?.items.length === 0}
      >
        <LogTable entries={logs.data?.items ?? []} />
      </Panel>
    </div>
  );
}

// --------------------------------------------------------------- Logs

/** 専用の列がある項目。行の中で重ねて出さない。 */
const REDUNDANT_FIELDS = new Set(["runId", "tenantId", "projectId", "sessionId"]);

/**
 * ログの付随フィールドを 1 行に並べる。
 *
 * メッセージだけでは「何が起きたか」が分からない。
 * `run created` に添えた runner / credential / 権限などが読めて初めて役に立つ。
 */
function LogFields({ fields }: { fields: Readonly<Record<string, unknown>> }): React.JSX.Element | null {
  const shown = Object.entries(fields).filter(
    ([key, value]) => !REDUNDANT_FIELDS.has(key) && value !== null && value !== undefined,
  );
  if (shown.length === 0) return null;

  return (
    <div className="log-fields mono">
      {shown.map(([key, value]) => (
        <span key={key} className="log-field">
          {key}={typeof value === "string" ? value : JSON.stringify(value)}
        </span>
      ))}
    </div>
  );
}

function LogTable({ entries }: { entries: readonly LogEntry[] }): React.JSX.Element {
  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>
            <th>時刻</th>
            <th>レベル</th>
            <th>メッセージ</th>
            <th>Run</th>
          </tr>
        </thead>
        <tbody>
          {entries.map((entry) => (
            <tr key={entry.id}>
              <td className="mono">{formatTimestamp(entry.ts)}</td>
              <td>
                <span className={`badge ${LEVEL_TONE[entry.level] ?? ""}`}>{entry.level}</span>
              </td>
              <td>
                {entry.message}
                <LogFields fields={entry.fields} />
              </td>
              <td className="mono">{entry.runId ?? "-"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * レベルの絞り込みは「以上」で扱う。
 *
 * 完全一致だと「info を選んだら warn / error が消える」ことになり、
 * 障害を探しているときに一番見たいものが落ちる。
 * 既定は `info`（`debug` のリクエストログが総量を支配するため）。
 */
const LOG_MIN_LEVELS = ["debug", "info", "warn", "error"] as const;
const DEFAULT_MIN_LEVEL = "info";

export function LogsView({ client }: { client: RcaClient }): React.JSX.Element {
  const [minLevel, setMinLevel] = useState<string>(DEFAULT_MIN_LEVEL);
  const [text, setText] = useState("");
  const [applied, setApplied] = useState({ minLevel: DEFAULT_MIN_LEVEL, text: "" });

  const { data, error, loading, reload } = useAsync(
    () =>
      client.admin.logs({
        minLevel: applied.minLevel,
        ...(applied.text === "" ? {} : { text: applied.text }),
        limit: 200,
      }),
    [applied.minLevel, applied.text],
  );

  return (
    <Panel
      title="ログ"
      error={error}
      loading={loading}
      empty={data?.items.length === 0}
      actions={
        <button type="button" onClick={reload}>
          再読込
        </button>
      }
    >
      <form
        className="filters"
        onSubmit={(e) => {
          e.preventDefault();
          setApplied({ minLevel, text });
        }}
      >
        <select
          aria-label="レベル"
          value={minLevel}
          onChange={(e) => setMinLevel(e.target.value)}
        >
          {LOG_MIN_LEVELS.map((value) => (
            <option key={value} value={value}>
              {value} 以上
            </option>
          ))}
        </select>
        <input
          aria-label="メッセージ検索"
          placeholder="メッセージを検索"
          value={text}
          onChange={(e) => setText(e.target.value)}
          style={{ width: 260 }}
        />
        <button type="submit">検索</button>
      </form>
      <LogTable entries={data?.items ?? []} />
    </Panel>
  );
}

// ------------------------------------------------------------ Metrics

export function MetricsView({ client }: { client: RcaClient }): React.JSX.Element {
  const { data, error, loading, reload } = useAsync(() => client.admin.metrics(), []);

  return (
    <Panel
      title="メトリクス"
      error={error}
      loading={loading}
      actions={
        <button type="button" onClick={reload}>
          再読込
        </button>
      }
    >
      {data !== null && <MetricsBody metrics={data} />}
    </Panel>
  );
}

function MetricsBody({ metrics }: { metrics: Metrics }): React.JSX.Element {
  /**
   * ★ 数は数え上げる。更新のたびに黙って差し替わると、
   * 「いま変わった」ことに気づけない。
   * 桁が揃う（tabular-nums）ので、動いている間も読める。
   */
  const tiles: [string, React.ReactNode][] = [
    ["Run 数", <NumberTicker key="runs" value={metrics.usage.runs} />],
    ["入力トークン", <NumberTicker key="in" value={metrics.usage.inputTokens} />],
    ["出力トークン", <NumberTicker key="out" value={metrics.usage.outputTokens} />],
    [
      "コスト",
      // 桁数は formatCost と同じ規則（1 ドル未満は 4 桁）。
      <NumberTicker
        key="cost"
        value={metrics.usage.costUsd}
        decimals={metrics.usage.costUsd < 1 ? 4 : 2}
        prefix="$"
      />,
    ],
    // 時間は単位が変わる（秒 / 分）ので、数え上げずそのまま出す。
    ["合計実行時間", formatDuration(metrics.usage.durationMs)],
  ];

  return (
    <div className="stack">
      <div className="row" data-testid="metric-tiles">
        {tiles.map(([label, value], i) => (
          <BlurFade key={label} delay={i * 0.05}>
            <div className="card" style={{ minWidth: 140 }}>
              <div className="muted" style={{ fontSize: 12 }}>
                {label}
              </div>
              <div style={{ fontSize: 20, fontWeight: 600 }}>{value}</div>
            </div>
          </BlurFade>
        ))}
      </div>

      <div className="card">
        <div className="muted" style={{ fontSize: 12, marginBottom: 6 }}>
          状態別（直近 {metrics.byStatusScannedRuns} 件を集計）
        </div>
        <div className="row">
          {Object.entries(metrics.byStatus).length === 0 ? (
            <span className="muted">まだ Run がありません</span>
          ) : (
            Object.entries(metrics.byStatus).map(([status, count]) => (
              <span key={status} className={`badge ${STATUS_TONE[status] ?? ""}`}>
                {status}: {count}
              </span>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

// -------------------------------------------------------------- Audit

export function AuditView({ client }: { client: RcaClient }): React.JSX.Element {
  const { data, error, loading } = useAsync(() => client.admin.audit(), []);

  return (
    <Panel title="監査ログ" error={error} loading={loading} empty={data?.items.length === 0}>
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>時刻</th>
              <th>操作</th>
              <th>対象</th>
              <th>実行者</th>
            </tr>
          </thead>
          <tbody>
            {(data?.items ?? []).map((entry: AuditEntry) => (
              <tr key={entry.id}>
                <td className="mono">{formatTimestamp(entry.createdAt)}</td>
                <td>{entry.action}</td>
                <td className="mono">
                  {entry.resourceType}/{entry.resourceId}
                </td>
                <td className="mono">{entry.actor}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Panel>
  );
}

// ------------------------------------------------------------ API キー

export function ApiKeysView({ client }: { client: RcaClient }): React.JSX.Element {
  const { data, error, loading, reload } = useAsync(() => client.admin.listApiKeys(), []);
  const [label, setLabel] = useState("");
  const [role, setRole] = useState<"admin" | "member">("member");
  // 平文は発行時にだけ返る。型でもそれを表す。
  const [issued, setIssued] = useState<IssuedApiKey | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const create = useCallback(async () => {
    setFormError(null);
    try {
      setIssued(await client.admin.createApiKey({ label, role }));
      setLabel("");
      reload();
    } catch (cause) {
      setFormError(cause instanceof RcaApiError ? cause.message : String(cause));
    }
  }, [client, label, reload, role]);

  return (
    <Panel title="API キー" error={error} loading={loading}>
      <div className="stack">
        {issued !== null && (
          <div className="card" data-testid="issued-key">
            <strong>発行しました。この画面を離れると二度と表示されません。</strong>
            <pre className="mono">{issued.plaintext}</pre>
            <button type="button" onClick={() => setIssued(null)}>
              閉じる
            </button>
          </div>
        )}

        {formError !== null && (
          <p className="error-banner" role="alert">
            {formError}
          </p>
        )}

        <form
          className="filters"
          onSubmit={(e) => {
            e.preventDefault();
            void create();
          }}
        >
          <input
            aria-label="ラベル"
            placeholder="用途（例: CI）"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            style={{ width: 220 }}
          />
          <select
            aria-label="ロール"
            value={role}
            onChange={(e) => setRole(e.target.value as "admin" | "member")}
          >
            <option value="member">member</option>
            <option value="admin">admin</option>
          </select>
          <button type="submit" className="primary" disabled={label.trim() === ""}>
            発行
          </button>
        </form>

        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>ラベル</th>
                <th>ロール</th>
                <th>スコープ</th>
                <th>状態</th>
                <th className="actions-col" />
              </tr>
            </thead>
            <tbody>
              {(data?.items ?? []).map((key) => (
                <tr key={key.id}>
                  <td>{key.label ?? key.id}</td>
                  <td>
                    <span className={`badge ${key.role === "admin" ? "warn" : ""}`}>{key.role}</span>
                  </td>
                  <td className="mono">{key.projectId ?? "-"}</td>
                  <td>
                    {key.revokedAt === null ? (
                      <span className="badge ok">有効</span>
                    ) : (
                      <span className="badge danger">失効</span>
                    )}
                  </td>
                  <td className="actions-cell">
                    <div className="actions">
                      {key.revokedAt === null && (
                        /* ★ 取り消せない。1 クリックでは起こさない。 */
                        <DangerButton
                          label="失効"
                          onConfirm={() => client.admin.revokeApiKey(key.id).then(reload)}
                        />
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Panel>
  );
}

// --------------------------------------------------------- Credentials

const CREDENTIAL_FIELDS: Record<string, { secret: string[]; publicFields: string[] }> = {
  anthropic: { secret: ["apiKey"], publicFields: ["baseUrl"] },
  openai: { secret: ["apiKey"], publicFields: ["baseUrl"] },
  "azure-openai": { secret: ["apiKey"], publicFields: ["endpoint", "deployment", "apiVersion"] },
  "google-vertex": { secret: ["serviceAccountJson"], publicFields: ["projectId", "region"] },
  git: { secret: ["token"], publicFields: ["username"] },
  // MCP アダプタが外部 REST API を叩くための鍵。
  // 接続先はアダプタ定義側が持つので、公開フィールドは無い。
  "api-key": { secret: ["apiKey"], publicFields: [] },
};

export function CredentialsView({
  client,
  place = TENANT_PLACE,
}: {
  client: RcaClient;
  place?: Place;
}): React.JSX.Element {
  const { data, error, loading, reload } = useAsync(
    () => client.credentials.list({ projectId: place.projectId }),
    [place.projectId],
  );
  const [id, setId] = useState("");
  const [kind, setKind] = useState("anthropic");
  const [name, setName] = useState("");
  const [values, setValues] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);

  const fields = CREDENTIAL_FIELDS[kind] ?? { secret: [], publicFields: [] };

  const submit = useCallback(async () => {
    setFormError(null);
    try {
      await client.credentials.create({
        // 空ならサーバ採番に任せる（キー自体を送らない）。
        ...(id.trim() === "" ? {} : { id: id.trim() }),
        // ★ 作る先は「いまいる場所」。画面から選ばせない。
        ...(place.projectId === null ? {} : { projectId: place.projectId }),
        kind,
        name,
        secret: Object.fromEntries(fields.secret.map((f) => [f, values[f] ?? ""])),
        publicFields: Object.fromEntries(
          fields.publicFields.filter((f) => (values[f] ?? "") !== "").map((f) => [f, values[f]!]),
        ),
      });
      setId("");
      setName("");
      setValues({});
      reload();
    } catch (cause) {
      setFormError(cause instanceof RcaApiError ? cause.message : String(cause));
    }
  }, [client, fields, id, kind, name, place.projectId, reload, values]);

  return (
    <Panel title="Credential" error={error} loading={loading}>
      <div className="stack">
        {formError !== null && (
          <p className="error-banner" role="alert">
            {formError}
          </p>
        )}

        <form
          className="stack card"
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
        >
          <div className="filters">
            <select aria-label="種別" value={kind} onChange={(e) => setKind(e.target.value)}>
              {Object.keys(CREDENTIAL_FIELDS).map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
            <input
              aria-label="名前"
              placeholder="名前（例: prod）"
              value={name}
              onChange={(e) => setName(e.target.value)}
              style={{ width: 200 }}
            />
            <input
              aria-label="ID"
              placeholder="ID（省略可・自動採番）"
              value={id}
              onChange={(e) => setId(e.target.value)}
              style={{ width: 200 }}
            />
          </div>

          {fields.secret.map((field) => (
            <input
              key={field}
              aria-label={field}
              type="password"
              placeholder={`${field}（保存後は表示されません）`}
              value={values[field] ?? ""}
              onChange={(e) => setValues((v) => ({ ...v, [field]: e.target.value }))}
            />
          ))}
          {fields.publicFields.map((field) => (
            <input
              key={field}
              aria-label={field}
              placeholder={field}
              value={values[field] ?? ""}
              onChange={(e) => setValues((v) => ({ ...v, [field]: e.target.value }))}
            />
          ))}

          <button type="submit" className="primary" disabled={name.trim() === ""}>
            登録
          </button>
        </form>

        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>名前</th>
                <th>階層</th>
                <th>種別</th>
                <th>公開フィールド</th>
                <th className="actions-col" />
              </tr>
            </thead>
            <tbody>
              {(data?.items ?? []).map((credential: CredentialView) => (
                <tr key={credential.id}>
                  <td>{credential.name}</td>
                  <td>
                    <OwnerBadge projectId={credential.projectId} />
                  </td>
                  <td>
                    <span className="badge">{credential.kind}</span>
                  </td>
                  <td className="mono">{JSON.stringify(credential.publicFields)}</td>
                  <td className="actions-cell">
                    {/* ★ 中身は必ず .actions で包む。裸のボタンは行ボックスの
                        strut を作り、ボタンのある行だけ 1px 高くなる。 */}
                    <div className="actions">
                      {/* ★ 上位のものはここからは消せない。テナントの画面で消す。 */}
                      {ownedHere(credential, place) && (
                        /* ★ これを消すと、指している Environment の Run が
                           全部止まる。確かめてから。 */
                        <DangerButton
                          label="削除"
                          onConfirm={() =>
                            client.credentials
                              .delete(credential.id, { projectId: place.projectId })
                              .then(reload)
                          }
                        />
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Panel>
  );
}

// ------------------------------------------------------- Environments

export function EnvironmentsView({
  client,
  place = TENANT_PLACE,
  userId = null,
}: {
  client: RcaClient;
  place?: Place;
  /** ログイン中の自分。user スコープの Environment はこれに紐づく。 */
  userId?: string | null;
}): React.JSX.Element {
  const { data, error, loading, reload } = useAsync(
    () => client.environments.list({ projectId: place.projectId }),
    [place.projectId],
  );
  const credentials = useAsync(
    () => client.credentials.list({ projectId: place.projectId }),
    [place.projectId],
  );
  const [selected, setSelected] = useState<EnvironmentDoc | null>(null);
  const [editing, setEditing] = useState<EnvironmentDoc | null>(null);
  const [creating, setCreating] = useState(false);

  const isEmpty = data !== null && data.items.length === 0;

  return (
    <Panel
      title="Environment"
      error={error}
      loading={loading}
      actions={
        <button
          type="button"
          className={isEmpty ? "primary" : ""}
          onClick={() => {
            setCreating((v) => !v);
            setEditing(null);
            setSelected(null);
          }}
        >
          Environment を作成
        </button>
      }
    >
      <div className="stack">
        {/*
          テナントは Zitadel 側で管理するので、新しい org は Environment を
          1 つも持たない状態で始まる。この状態では Run が
          `environment_not_found` で止まるため、ここで気づけるようにする。
        */}
        {isEmpty && !creating && (
          <p className="empty">
            Environment がありません。1 つ作るまで Run は実行できません
            （<code>environment_not_found</code> になります）。
          </p>
        )}

        {creating && (
          <EnvironmentCreator
            client={client}
            place={place}
            userId={userId}
            credentials={credentials.data?.items ?? []}
            onClose={() => setCreating(false)}
            onCreated={() => {
              setCreating(false);
              reload();
            }}
          />
        )}
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>名前</th>
                <th>階層</th>
                <th>スコープ</th>
                <th>Runner</th>
                <th>権限</th>
                <th>Credential</th>
                <th>継承</th>
                <th>版</th>
                <th className="actions-col" />
              </tr>
            </thead>
            <tbody>
              {(data?.items ?? []).map((doc) => (
                <tr key={doc.id}>
                  <td>{doc.name}</td>
                  <td>
                    <OwnerBadge projectId={doc.projectId} />
                  </td>
                  <td className="mono">
                    {doc.scope}
                    {doc.projectId === null ? "" : `/${doc.projectId}`}
                    {doc.userId === null ? "" : `/${doc.userId}`}
                  </td>
                  <td className="mono">{runnerTypeOf(doc) ?? "-"}</td>
                  <td className="mono">{permissionModeOf(doc) ?? "default"}</td>
                  <td className="mono">{credentialIdOf(doc) ?? "-"}</td>
                  <td>{doc.inherit ? "する" : "しない"}</td>
                  <td className="mono">v{doc.version}</td>
                  <td className="actions-cell">
                    <div className="actions">
                      <button
                        type="button"
                        onClick={() => {
                          setEditing(doc);
                          setSelected(null);
                        }}
                      >
                        編集
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setSelected(doc);
                          setEditing(null);
                        }}
                      >
                        spec を見る
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {editing !== null && (
          <EnvironmentEditor
            client={client}
            doc={editing}
            credentials={credentials.data?.items ?? []}
            onClose={() => setEditing(null)}
            onSaved={() => {
              setEditing(null);
              reload();
            }}
          />
        )}

        {selected !== null && (
          <div className="card">
            <div className="row">
              <strong>{selected.name}</strong>
              <span className="spacer" style={{ flex: 1 }} />
              <button type="button" onClick={() => setSelected(null)}>
                閉じる
              </button>
            </div>
            <pre className="mono">{JSON.stringify(selected.spec, null, 2)}</pre>
          </div>
        )}
      </div>
    </Panel>
  );
}

const RUNNER_TYPES = ["claude-code", "codex"] as const;
const SANDBOX_PROVIDERS = ["memory", "docker", "cloudflare"] as const;
const SCOPES = ["tenant", "project", "user"] as const;

/**
 * ツールの実行許可（`--permission-mode` / codex の `sandbox_mode`）。
 *
 * API は非対話で回るので、**対話的に許可を出す手段が無い**。
 * `default` のままだと承認待ちになるツールが軒並み使えず、
 * エージェントが読むだけで終わる。隔離コンテナで使い捨てるのが前提なら
 * `bypassPermissions` を選ぶ。
 */
const PERMISSION_MODES = [
  { value: "default", label: "default（都度承認。非対話では実質使えない）" },
  { value: "auto", label: "auto（自動判断。root でも使える）" },
  { value: "acceptEdits", label: "acceptEdits（ファイル編集を自動許可）" },
  { value: "bypassPermissions", label: "bypassPermissions（全許可 / root 不可）" },
  { value: "dontAsk", label: "dontAsk（確認しない）" },
  { value: "plan", label: "plan（計画のみ。変更しない）" },
] as const;

/** 承認待ちで止まってしまい、非対話実行では実質ツールが使えないモード。 */
const BLOCKS_TOOLS = new Set(["default"]);

/** spec は自由形なので、必要な枝だけを型を絞って読む。 */
function specBranch(doc: EnvironmentDoc, key: string): Record<string, unknown> | null {
  const value = doc.spec[key];
  return typeof value === "object" && value !== null ? (value as Record<string, unknown>) : null;
}

/**
 * いま紐づいているバンドル ID。
 *
 * スコープを跨ぐと上位から追記されるが、ここで見るのは
 * **この Environment 自身が持っている指定**だけ（編集対象がそれのため）。
 */
export function bundleIdsOf(doc: EnvironmentDoc): string[] {
  const bundles = specBranch(doc, "contextAssets")?.["bundles"];
  if (!Array.isArray(bundles)) return [];
  return bundles
    .map((ref) => (ref as { bundleId?: unknown }).bundleId)
    .filter((id): id is string => typeof id === "string");
}

export function credentialIdOf(doc: EnvironmentDoc): string | null {
  const value = specBranch(doc, "credential")?.["credentialId"];
  return typeof value === "string" ? value : null;
}

export function runnerTypeOf(doc: EnvironmentDoc): string | null {
  const value = specBranch(doc, "runner")?.["type"];
  return typeof value === "string" ? value : null;
}

export function permissionModeOf(doc: EnvironmentDoc): string | null {
  const value = specBranch(doc, "runner")?.["permissionMode"];
  return typeof value === "string" ? value : null;
}

export function allowedToolsOf(doc: EnvironmentDoc): string[] {
  const value = specBranch(doc, "runner")?.["allowedTools"];
  return Array.isArray(value) ? value.filter((v): v is string => typeof v === "string") : [];
}

/**
 * Environment を新規作成する（FR-W5.1）。
 *
 * テナントは Zitadel 側で管理するので、**新しい org はここが空の状態で始まる**。
 * 画面から 1 つも作れないと、Run は永久に `environment_not_found` で止まる。
 */
/**
 * Environment を作る。
 *
 * ★ スコープの ID を**手で打たせない**。`projectId` は「いまいる場所」で決まり、
 * `userId` はログイン中の自分（Zitadel の sub）。打たせると、
 * 打ち間違えた ID のスコープが黙って生まれる。
 */
export function EnvironmentCreator({
  client,
  credentials,
  onClose,
  onCreated,
  place = TENANT_PLACE,
  /** ログイン中の自分。user スコープはこれに紐づく。 */
  userId = null,
}: {
  client: RcaClient;
  credentials: readonly CredentialView[];
  onClose: () => void;
  onCreated: () => void;
  place?: Place;
  userId?: string | null;
}): React.JSX.Element {
  const [name, setName] = useState("");
  // ★ 選べるスコープは居場所で決まる。テナント直下では tenant しか作れない。
  const scopeChoices = place.projectId === null ? (["tenant"] as const) : (["project", "user"] as const);
  const [scope, setScope] = useState<(typeof SCOPES)[number]>(scopeChoices[0]);
  const projectId = place.projectId ?? "";
  const [sandbox, setSandbox] = useState<string>(SANDBOX_PROVIDERS[0]);
  const [runner, setRunner] = useState<string>(RUNNER_TYPES[0]);
  const [credentialId, setCredentialId] = useState("");
  const [permissionMode, setPermissionMode] = useState<string>(PERMISSION_MODES[0].value);
  const [allowedTools, setAllowedTools] = useState("");
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const needsProject = scope === "project" || scope === "user";
  const needsUser = scope === "user";
  const ready =
    name.trim() !== "" && (!needsProject || projectId !== "") && (!needsUser || userId !== null);

  const create = useCallback(async () => {
    setSaving(true);
    setFormError(null);
    try {
      await client.environments.put({
        // id はサーバに採番させる。
        name: name.trim(),
        scope,
        ...(needsProject ? { projectId } : {}),
        ...(needsUser && userId !== null ? { userId } : {}),
        // 既定は上位スコープとマージする（仕様の既定）。
        inherit: true,
        spec: {
          sandbox: { provider: sandbox },
          runner: {
            type: runner,
            permissionMode,
            allowedTools: allowedTools
              .split(",")
              .map((tool) => tool.trim())
              .filter((tool) => tool !== ""),
          },
          ...(credentialId === "" ? {} : { credential: { credentialId } }),
        },
      });
      onCreated();
    } catch (cause) {
      setFormError(cause instanceof RcaApiError ? cause.message : String(cause));
    } finally {
      setSaving(false);
    }
  }, [
    allowedTools,
    client,
    credentialId,
    name,
    needsProject,
    needsUser,
    onCreated,
    permissionMode,
    projectId,
    runner,
    sandbox,
    scope,
    userId,
  ]);

  return (
    <div className="card stack">
      <div className="row">
        <strong>Environment を作成</strong>
        <span className="spacer" style={{ flex: 1 }} />
        <button type="button" onClick={onClose}>
          閉じる
        </button>
      </div>

      {formError !== null && (
        <p className="error-banner" role="alert">
          {formError}
        </p>
      )}

      {credentials.length === 0 && (
        <p className="error-banner" role="alert">
          Credential がまだありません。先に「Credential」画面で登録してください
          （無いまま作ると Run は実行時に失敗します）。
        </p>
      )}

      <div className="filters">
        <label htmlFor="env-new-name">名前</label>
        <input
          id="env-new-name"
          placeholder="例: default"
          value={name}
          onChange={(e) => setName(e.target.value)}
          style={{ width: 180 }}
        />

        <label htmlFor="env-new-scope">スコープ</label>
        <select
          id="env-new-scope"
          value={scope}
          onChange={(e) => setScope(e.target.value as (typeof SCOPES)[number])}
        >
          {scopeChoices.map((value) => (
            <option key={value} value={value}>
              {value === "tenant"
                ? "テナント全体"
                : value === "project"
                  ? "このプロジェクト"
                  : "このプロジェクトでの自分"}
            </option>
          ))}
        </select>

        {/* ★ ID は表示するだけ。打ち込ませない。 */}
        {needsProject && (
          <span className="mono" data-testid="env-new-project">
            {projectId}
          </span>
        )}
        {needsUser && (
          <span className="mono" data-testid="env-new-user">
            {userId ?? "（ログイン情報がありません）"}
          </span>
        )}
      </div>

      <div className="filters">
        <label htmlFor="env-new-sandbox">Sandbox</label>
        <select
          id="env-new-sandbox"
          value={sandbox}
          onChange={(e) => setSandbox(e.target.value)}
        >
          {SANDBOX_PROVIDERS.map((value) => (
            <option key={value} value={value}>
              {value}
            </option>
          ))}
        </select>

        <label htmlFor="env-new-runner">Runner</label>
        <select id="env-new-runner" value={runner} onChange={(e) => setRunner(e.target.value)}>
          {RUNNER_TYPES.map((value) => (
            <option key={value} value={value}>
              {value}
            </option>
          ))}
        </select>

        <label htmlFor="env-new-credential">Credential</label>
        <select
          id="env-new-credential"
          value={credentialId}
          onChange={(e) => setCredentialId(e.target.value)}
        >
          <option value="">（未設定）</option>
          {credentials.map((item) => (
            <option key={item.id} value={item.id}>
              {item.name}（{item.id} / {item.kind}）
            </option>
          ))}
        </select>
      </div>

      <div className="filters">
        <label htmlFor="env-new-permission">権限</label>
        <select
          id="env-new-permission"
          value={permissionMode}
          onChange={(e) => setPermissionMode(e.target.value)}
        >
          {PERMISSION_MODES.map((mode) => (
            <option key={mode.value} value={mode.value}>
              {mode.label}
            </option>
          ))}
        </select>

        <input
          aria-label="許可ツール"
          placeholder="例: Bash(git *), Edit（カンマ区切り・任意）"
          value={allowedTools}
          onChange={(e) => setAllowedTools(e.target.value)}
          style={{ width: 260 }}
        />

        <button
          type="button"
          className="primary"
          disabled={!ready || saving}
          onClick={() => void create()}
        >
          作成する
        </button>
      </div>
    </div>
  );
}

/**
 * Environment の Runner と Credential を差し替える（FR-W5.1）。
 *
 * **id を手で打たせない。** UI から登録した Credential は id が自動採番されるので、
 * Environment が古い id を指したままだと Run 作成が 403 で止まる。
 * 実際にそれで詰まったので、選択式にして現状とのズレも警告する。
 */
export function EnvironmentEditor({
  client,
  doc,
  credentials,
  onClose,
  onSaved,
}: {
  client: RcaClient;
  doc: EnvironmentDoc;
  credentials: readonly CredentialView[];
  onClose: () => void;
  onSaved: () => void;
}): React.JSX.Element {
  const current = credentialIdOf(doc);
  const [credentialId, setCredentialId] = useState(current ?? "");
  const [runner, setRunner] = useState(runnerTypeOf(doc) ?? RUNNER_TYPES[0]);
  // 未設定は CLI 側の既定（default）と同じ意味なので、そう表示する。
  const [permissionMode, setPermissionMode] = useState(
    permissionModeOf(doc) ?? PERMISSION_MODES[0].value,
  );
  const [allowedTools, setAllowedTools] = useState(allowedToolsOf(doc).join(", "));
  const [bundleIds, setBundleIds] = useState<readonly string[]>(bundleIdsOf(doc));
  // 候補は id を手で打たせず一覧から選ばせる（Credential と同じ方針）。
  const bundles = useAsync(() => client.bundles.list(), [client]);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const dangling = current !== null && !credentials.some((c) => c.id === current);

  const save = useCallback(async () => {
    setSaving(true);
    setFormError(null);
    try {
      // 触っていない項目は消さない。spec 全体を持ち回して該当箇所だけ差し替える。
      await client.environments.put({
        id: doc.id,
        name: doc.name,
        scope: doc.scope,
        projectId: doc.projectId,
        userId: doc.userId,
        inherit: doc.inherit,
        spec: {
          ...doc.spec,
          runner: {
            ...(specBranch(doc, "runner") ?? {}),
            type: runner,
            permissionMode,
            allowedTools: allowedTools
              .split(",")
              .map((tool) => tool.trim())
              .filter((tool) => tool !== ""),
          },
          credential: { ...(specBranch(doc, "credential") ?? {}), credentialId },
          // 上位から引き継いだ分は追記されるので、ここは自分の指定だけを持つ。
          contextAssets: {
            ...(specBranch(doc, "contextAssets") ?? {}),
            bundles: bundleIds.map((bundleId) => ({ bundleId })),
          },
        },
      });
      onSaved();
    } catch (cause) {
      setFormError(cause instanceof RcaApiError ? cause.message : String(cause));
    } finally {
      setSaving(false);
    }
  }, [allowedTools, bundleIds, client, credentialId, doc, onSaved, permissionMode, runner]);

  return (
    <div className="card stack">
      <div className="row">
        <strong>{doc.name} を編集</strong>
        <span className="spacer" style={{ flex: 1 }} />
        <button type="button" onClick={onClose}>
          閉じる
        </button>
      </div>

      {formError !== null && (
        <p className="error-banner" role="alert">
          {formError}
        </p>
      )}

      {dangling && (
        <p className="error-banner" role="alert">
          いま指している Credential <code>{current}</code> は登録されていません。
          このままだと実行が 403 で止まります。下の一覧から選び直してください。
        </p>
      )}

      {BLOCKS_TOOLS.has(permissionMode) && allowedTools.trim() === "" && (
        <p className="error-banner" role="alert">
          権限が <code>default</code> で、許可ツールも空です。API 実行には対話的に許可を出す
          手段が無いため、承認が必要なツール（ファイル編集やコマンド実行）は使えません。
          隔離コンテナで動かすなら <code>bypassPermissions</code>、root で動く環境なら
          <code>auto</code> を選ぶか、下の「許可ツール」に必要なものを並べてください。
        </p>
      )}

      {permissionMode === "bypassPermissions" && (
        <p className="muted" style={{ margin: 0, fontSize: 12 }}>
          ※ claude-code は <code>bypassPermissions</code> を <strong>root では拒否</strong>します
          （回避フラグはありません）。コンテナが root で動く環境では <code>auto</code> か
          「許可ツール」を使ってください。ローカルの Docker モードは非 root なので使えます。
        </p>
      )}

      {credentials.length === 0 && (
        <p className="muted">
          Credential がまだありません。「Credential」画面で登録してから戻ってきてください。
        </p>
      )}

      <div className="filters">
        <label htmlFor="env-runner">Runner</label>
        <select id="env-runner" value={runner} onChange={(e) => setRunner(e.target.value)}>
          {RUNNER_TYPES.map((value) => (
            <option key={value} value={value}>
              {value}
            </option>
          ))}
        </select>

        <label htmlFor="env-permission">権限</label>
        <select
          id="env-permission"
          value={permissionMode}
          onChange={(e) => setPermissionMode(e.target.value)}
        >
          {PERMISSION_MODES.map((mode) => (
            <option key={mode.value} value={mode.value}>
              {mode.label}
            </option>
          ))}
        </select>

        <label htmlFor="env-credential">Credential</label>
        <select
          id="env-credential"
          value={credentialId}
          onChange={(e) => setCredentialId(e.target.value)}
        >
          {/* 現状が候補に無い場合も、選択が勝手に変わらないよう残しておく。 */}
          {dangling && <option value={current ?? ""}>{current}（未登録）</option>}
          {credentials.map((item) => (
            <option key={item.id} value={item.id}>
              {item.name}（{item.id} / {item.kind}）
            </option>
          ))}
        </select>

        <label htmlFor="env-allowed-tools">許可ツール</label>
        <input
          id="env-allowed-tools"
          placeholder="例: Bash(git *), Edit, Write（カンマ区切り）"
          value={allowedTools}
          onChange={(e) => setAllowedTools(e.target.value)}
          style={{ width: 280 }}
        />

        <button type="button" className="primary" onClick={() => void save()} disabled={saving}>
          保存
        </button>
      </div>

      <div className="stack">
        <strong>資産バンドル</strong>
        <p className="muted" style={{ margin: 0, fontSize: 12 }}>
          instructions / Skill / subagent / MCP をこの Environment に配ります。
          上位スコープの指定は自動で引き継がれるので、ここでは**この Environment 自身の分**を選びます。
        </p>
        {bundles.loading && <Skeleton rows={2} height={14} />}
        {!bundles.loading && (bundles.data?.items ?? []).length === 0 && (
          <p className="muted">
            バンドルがまだありません。「資産バンドル」画面で作ってから戻ってきてください。
          </p>
        )}
        {(bundles.data?.items ?? []).map((item) => {
          const checked = bundleIds.includes(item.id);
          return (
            <label key={item.id} htmlFor={`env-bundle-${item.id}`}>
              <input
                id={`env-bundle-${item.id}`}
                type="checkbox"
                checked={checked}
                onChange={() =>
                  setBundleIds((prev) =>
                    checked ? prev.filter((id) => id !== item.id) : [...prev, item.id],
                  )
                }
              />
              {item.name}（{item.id}）
              {item.latestVersion === "" && <span className="muted">（未投入）</span>}
            </label>
          );
        })}
      </div>
    </div>
  );
}

/** JSON の配列として読めれば値、読めなければ null。 */
function parseJsonArrayOrNull(text: string): Record<string, unknown>[] | null {
  try {
    const value: unknown = JSON.parse(text);
    return Array.isArray(value) ? (value as Record<string, unknown>[]) : null;
  } catch {
    return null;
  }
}

/** JSON として読めれば値、読めなければ null。保存ボタンの可否に使う。 */
function parseJsonOrNull(text: string): Record<string, unknown> | null {
  try {
    const value: unknown = JSON.parse(text);
    return typeof value === "object" && value !== null && !Array.isArray(value)
      ? (value as Record<string, unknown>)
      : null;
  } catch {
    return null;
  }
}

/**
 * テナントの一覧。
 *
 * ★ 見えるのは**自分のテナントだけ**（API 側で絞っている）。
 * 横断で見せると顧客名が他社に漏れる。
 */
export function TenantsView({ client }: { client: RcaClient }): React.JSX.Element {
  const { data, error, loading } = useAsync(() => client.tenants.list(), []);
  const items = data?.items ?? [];

  return (
    <section className="stack">
      <header className="row">
        <h2>テナント</h2>
      </header>
      {error !== null && (
        <p className="error-banner" role="alert">
          {String(error)}
        </p>
      )}
      {loading && <Skeleton rows={3} height={14} />}
      {!loading && items.length === 0 && <p className="muted">テナントがありません。</p>}
      {items.length > 0 && (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>名前</th>
                <th>ID</th>
              </tr>
            </thead>
            <tbody>
              {items.map((tenant) => (
                <tr key={tenant.id}>
                  <td>{tenant.name}</td>
                  <td className="mono">{tenant.id}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

// ------------------------------------------------------------ Project

/**
 * テナントの下の入れ物（FR-1）。
 *
 * ★ 削除は**取り消せない**。属する Environment・バンドル・資格情報・Run が
 * すべて消え、秘密の実体まで消える。名前を打ち直させて確かめる。
 */
export function ProjectsView({
  client,
  currentProjectId = null,
  onOpen,
}: {
  client: RcaClient;
  currentProjectId?: string | null;
  onOpen?: (projectId: string | null) => void;
}): React.JSX.Element {
  const { data, error, loading, reload } = useAsync(() => client.projects.list(), []);
  const [name, setName] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  /** 削除の確認中。名前が一致するまで消さない。 */
  const [confirming, setConfirming] = useState<ProjectView | null>(null);
  const [typed, setTyped] = useState("");

  const create = useCallback(async () => {
    setFormError(null);
    try {
      await client.projects.create({ name: name.trim() });
      setName("");
      reload();
    } catch (cause) {
      setFormError(cause instanceof RcaApiError ? cause.message : String(cause));
    }
  }, [client, name, reload]);

  const remove = useCallback(
    async (project: ProjectView) => {
      setFormError(null);
      try {
        await client.projects.delete(project.id);
        setConfirming(null);
        setTyped("");
        // いま開いているプロジェクトを消したら、テナントの居場所へ戻す。
        if (project.id === currentProjectId) onOpen?.(null);
        reload();
      } catch (cause) {
        setFormError(cause instanceof RcaApiError ? cause.message : String(cause));
      }
    },
    [client, currentProjectId, onOpen, reload],
  );

  const isEmpty = data !== null && data.items.length === 0;

  return (
    <Panel title="プロジェクト" error={error} loading={loading}>
      <div className="stack">
        {formError !== null && (
          <p className="error-banner" role="alert">
            {formError}
          </p>
        )}

        {isEmpty && (
          <p className="empty">
            プロジェクトがありません。作らなくてもテナント全体として使えます。
            案件ごとに鍵や設定を分けたいときに作ります。
          </p>
        )}

        <form
          className="card filters"
          onSubmit={(e) => {
            e.preventDefault();
            void create();
          }}
        >
          <label htmlFor="project-name">名前</label>
          <input
            id="project-name"
            placeholder="例: 見積もり自動化"
            value={name}
            onChange={(e) => setName(e.target.value)}
            style={{ width: 240 }}
          />
          <button type="submit" className="primary" disabled={name.trim() === ""}>
            作成
          </button>
        </form>

        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>名前</th>
                <th>ID</th>
                <th>作成</th>
                <th className="actions-col" />
              </tr>
            </thead>
            <tbody>
              {(data?.items ?? []).map((project) => (
                <tr key={project.id}>
                  <td>
                    {project.name}
                    {project.id === currentProjectId && (
                      <span className="badge accent" style={{ marginLeft: 6 }}>
                        いま
                      </span>
                    )}
                  </td>
                  <td className="mono">{project.id}</td>
                  <td className="mono">{formatTimestamp(project.createdAt)}</td>
                  <td className="actions-cell">
                    <div className="actions">
                      <button type="button" onClick={() => onOpen?.(project.id)}>
                        開く
                      </button>
                      <button
                        type="button"
                        className="danger"
                        onClick={() => {
                          setConfirming(project);
                          setTyped("");
                        }}
                      >
                        削除
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {confirming !== null && (
          <div className="card stack" role="alertdialog" aria-label="プロジェクトの削除">
            <strong>「{confirming.name}」を削除します</strong>
            <p>
              ★ <b>取り消せません。</b>
              このプロジェクトの Environment・資産バンドル・Credential・Run・セッションが
              すべて消えます。Credential は秘密の実体も消えます。
            </p>
            <div className="filters">
              <label htmlFor="project-confirm">確認のため名前を入力</label>
              <input
                id="project-confirm"
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                style={{ width: 240 }}
              />
              <button
                type="button"
                className="danger"
                disabled={typed !== confirming.name}
                onClick={() => void remove(confirming)}
              >
                完全に削除する
              </button>
              <button type="button" onClick={() => setConfirming(null)}>
                やめる
              </button>
            </div>
          </div>
        )}
      </div>
    </Panel>
  );
}

// ------------------------------------------------------- 資産バンドル

/**
 * Skills / instructions / MCP の配布（FR-7）。
 *
 * 更新は **PATCH で差分だけ**送る。Skill を 1 つ足すのに
 * 全部入れ直させると、手元に無いものを消す事故が起きる。
 */
export function BundlesView({
  client,
  place = TENANT_PLACE,
}: {
  client: RcaClient;
  place?: Place;
}): React.JSX.Element {
  const { data, error, loading, reload } = useAsync(
    () => client.bundles.list({ projectId: place.projectId }),
    [place.projectId],
  );
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<BundleView | null>(null);
  const [id, setId] = useState("");
  const [name, setName] = useState("");
  const [formError, setFormError] = useState<string | null>(null);

  const isEmpty = data !== null && data.items.length === 0;

  const create = useCallback(async () => {
    setFormError(null);
    try {
      // ★ 作る先は「いまいる場所」。
      await client.bundles.create({
        id: id.trim(),
        name: name.trim(),
        ...(place.projectId === null ? {} : { projectId: place.projectId }),
      });
      setId("");
      setName("");
      setCreating(false);
      reload();
    } catch (cause) {
      setFormError(cause instanceof RcaApiError ? cause.message : String(cause));
    }
  }, [client, id, name, place.projectId, reload]);

  return (
    <Panel
      title="資産バンドル"
      error={error}
      loading={loading}
      actions={
        <button
          type="button"
          className={isEmpty ? "primary" : ""}
          onClick={() => setCreating((v) => !v)}
        >
          バンドルを作成
        </button>
      }
    >
      <div className="stack">
        {formError !== null && (
          <p className="error-banner" role="alert">
            {formError}
          </p>
        )}

        {isEmpty && !creating && (
          <p className="empty">
            バンドルがありません。Skill や <code>CLAUDE.md</code> を配るにはここで作ります。
          </p>
        )}

        {creating && (
          <div className="card filters">
            <label htmlFor="bundle-id">ID</label>
            <input
              id="bundle-id"
              placeholder="例: team"
              value={id}
              onChange={(e) => setId(e.target.value)}
              style={{ width: 160 }}
            />
            <label htmlFor="bundle-name">名前</label>
            <input
              id="bundle-name"
              placeholder="例: チーム標準"
              value={name}
              onChange={(e) => setName(e.target.value)}
              style={{ width: 200 }}
            />
            <button
              type="button"
              className="primary"
              disabled={id.trim() === "" || name.trim() === ""}
              onClick={() => void create()}
            >
              作成する
            </button>
          </div>
        )}

        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>名前</th>
                <th>階層</th>
                <th>ID</th>
                <th>最新版</th>
                <th className="actions-col" />
              </tr>
            </thead>
            <tbody>
              {(data?.items ?? []).map((bundle) => (
                <tr key={bundle.id}>
                  <td>{bundle.name}</td>
                  <td>
                    <OwnerBadge projectId={bundle.projectId} />
                  </td>
                  <td className="mono">{bundle.id}</td>
                  <td className="mono">
                    {bundle.latestVersion === "" ? "（未投入）" : bundle.latestVersion.slice(0, 12)}
                  </td>
                  <td className="actions-cell">
                    <div className="actions">
                      <button type="button" onClick={() => setEditing(bundle)}>
                        編集
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {editing !== null && (
          <BundleEditor
            client={client}
            place={place}
            bundle={editing}
            onClose={() => setEditing(null)}
            onSaved={() => {
              setEditing(null);
              reload();
            }}
          />
        )}
      </div>
    </Panel>
  );
}

/**
 * ドロワーの 1 段目。いま入っているものを並べ、選ばせる。
 *
 * ★ 「何が入っているか」と「直す口」を同じ場所に置く。
 * 別々の場所にあると、直したいものを選べないまま空欄と向き合うことになる。
 */
function ItemList({
  kind,
  items,
  onNew,
  loading,
  disabled,
}: {
  kind: string;
  items: readonly {
    key: string;
    label: string;
    hint?: string;
    onEdit: () => void;
    /**
     * 消す。
     *
     * ★ **約束を返すこと。** `void` で捨てると、走っている間が分からず
     * 連打できる（`DangerButton` が待てない）。
     */
    onRemove: () => Promise<unknown>;
  }[];
  onNew: () => void;
  loading: boolean;
  disabled: boolean;
}): React.JSX.Element {
  return (
    <div className="stack">
      <button type="button" className="primary" onClick={onNew} style={{ alignSelf: "flex-start" }}>
        新しく作る
      </button>

      {loading && <Skeleton rows={3} height={14} />}
      {!loading && items.length === 0 && (
        <p className="empty">{kind} はまだありません。「新しく作る」から追加します。</p>
      )}

      {items.length > 0 && (
        <div className="table-wrap">
          <table>
            <tbody>
              {items.map((item) => (
                <tr key={item.key}>
                  <td>
                    {item.label}
                    {item.hint !== undefined && (
                      <span className="badge" style={{ marginLeft: 6 }}>
                        {item.hint}
                      </span>
                    )}
                  </td>
                  <td className="actions-cell">
                    <div className="actions">
                      <button type="button" disabled={disabled} onClick={item.onEdit}>
                        編集
                      </button>
                      {/* ★ ここにあった 2 段を共通の部品に寄せた。
                          画面ごとに作法が違うと、どこで確かめが出るのか
                          覚えられない。 */}
                      <DangerButton label="削除" disabled={disabled} onConfirm={item.onRemove} />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function BundleEditor({
  client,
  bundle,
  onClose,
  onSaved,
  place = TENANT_PLACE,
}: {
  client: RcaClient;
  bundle: BundleView;
  onClose: () => void;
  onSaved: () => void;
  /** ★ 版を触る操作にも居場所が要る。渡さないとプロジェクトのバンドルが 404 になる。 */
  place?: Place;
}): React.JSX.Element {
  const versions = useAsync(
    () => client.bundles.listVersions(bundle.id, place),
    [bundle.id, place.projectId],
  );
  /**
   * ★ いま入っている中身を読んでから始める。
   *
   * 以前は常に空欄から始まり、既存を見ながら直せなかった
   * （「Skill を 1 行だけ直す」ができず、毎回書き直して上書きしていた）。
   */
  const current = useAsync(
    () =>
      bundle.latestVersion === ""
        ? Promise.resolve(null)
        : client.bundles.getVersion(bundle.id, "latest", place).catch(() => null),
    [bundle.id, bundle.latestVersion, place.projectId],
  );
  const [instructions, setInstructions] = useState("");
  const [skillName, setSkillName] = useState("");
  const [skillBody, setSkillBody] = useState("");
  /**
   * ★ SKILL.md 以外のファイルは、編集画面に出ないまま保持する。
   *
   * PATCH は Skill を名前ごと差し替えるので、持ち回らないと
   * 「1 行直したら添付ファイルが消えた」ことになる。
   */
  const [skillExtraFiles, setSkillExtraFiles] = useState<
    readonly { path: string; content: string }[]
  >([]);
  /** いま直しているもの。null なら新規。 */
  const [editingSkill, setEditingSkill] = useState<string | null>(null);
  const [editingAgent, setEditingAgent] = useState<{ runner: string; name: string } | null>(null);
  /**
   * ドロワーの段階。
   *
   * ★ **一覧 → 編集**の 2 段にする。いきなり空欄の入力欄を出すと、
   * 「いま何が入っているか」が別の場所にあり、直したいものを選べない。
   */
  const [skillStep, setSkillStep] = useState<"list" | "edit">("list");
  const [agentStep, setAgentStep] = useState<"list" | "edit">("list");
  const [agentRunner, setAgentRunner] = useState<"claude-code" | "codex">("claude-code");
  const [agentName, setAgentName] = useState("");
  const [agentDescription, setAgentDescription] = useState("");
  const [agentPrompt, setAgentPrompt] = useState("");
  const [agentModel, setAgentModel] = useState("");
  const [agentTools, setAgentTools] = useState("");
  const [agentSandbox, setAgentSandbox] = useState("");
  /**
   * MCP は 1 ファイルで丸ごと差し替わるので、全サーバをまとめて扱う。
   * **開くまで送らない。** 触っていないのに送ると、伏せ字のまま往復させる意味が無い。
   */
  const [mcpOpen, setMcpOpen] = useState(false);
  const [mcpText, setMcpText] = useState("");
  /** アダプタも 1 まとまりで扱う。開くまで送らない。 */
  /** 開いているパネル。同時に 1 つだけ開く。 */
  const [openPanel, setOpenPanel] = useState<
    "instructions" | "skill" | "agent" | "mcp" | "adapters" | null
  >(null);
  const [adaptersOpen, setAdaptersOpen] = useState(false);
  const [adaptersText, setAdaptersText] = useState("[]");
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  /** 新規として開く。前に開いていたものを引きずらない。 */
  const startNewSkill = useCallback(() => {
    setEditingSkill(null);
    setSkillName("");
    setSkillBody("");
    setSkillExtraFiles([]);
    setSkillStep("edit");
  }, []);

  /** ★ 既存を読み込んで開く。これが無いと、直すのに全部打ち直しになる。 */
  const startEditSkill = useCallback((skill: BundleSkill) => {
    setEditingSkill(skill.name);
    setSkillName(skill.name);
    setSkillBody(skill.files.find((f) => f.path === "SKILL.md")?.content ?? "");
    setSkillExtraFiles(
      skill.files
        .filter((f) => f.path !== "SKILL.md")
        .map((f) => ({ path: f.path, content: f.content ?? "" })),
    );
    setSkillStep("edit");
  }, []);

  const startNewAgent = useCallback(() => {
    setEditingAgent(null);
    setAgentRunner("claude-code");
    setAgentName("");
    setAgentDescription("");
    setAgentPrompt("");
    setAgentModel("");
    setAgentTools("");
    setAgentSandbox("");
    setAgentStep("edit");
  }, []);

  const startEditAgent = useCallback((agent: BundleAgent) => {
    setEditingAgent({ runner: agent.runner, name: agent.name });
    setAgentRunner(agent.runner);
    setAgentName(agent.name);
    setAgentDescription(agent.description);
    setAgentPrompt(agent.prompt);
    setAgentModel(agent.model ?? "");
    setAgentTools((agent.tools ?? []).join(", "));
    setAgentSandbox(agent.sandboxMode ?? "");
    setAgentStep("edit");
  }, []);

  // 読み込めたら編集欄の初期値にする。
  useEffect(() => {
    if (current.data != null) {
      setInstructions(current.data.instructions ?? "");
      setMcpText(JSON.stringify(current.data.mcpServers ?? {}, null, 2));
      setAdaptersText(JSON.stringify(current.data.adapters ?? [], null, 2));
    }
  }, [current.data]);

  const send = useCallback(
    async (body: BundlePatch) => {
      setSaving(true);
      setFormError(null);
      try {
        await client.bundles.patchVersion(bundle.id, body, place);
        onSaved();
      } catch (cause) {
        setFormError(cause instanceof RcaApiError ? cause.message : String(cause));
      } finally {
        setSaving(false);
      }
    },
    [bundle.id, client, onSaved],
  );

  return (
    <div className="card stack">
      <div className="row">
        <strong>{bundle.name} を編集</strong>
        <span className="spacer" style={{ flex: 1 }} />
        <button type="button" onClick={onClose}>
          閉じる
        </button>
      </div>

      {formError !== null && (
        <p className="error-banner" role="alert">
          {formError}
        </p>
      )}

      <p className="muted" style={{ margin: 0, fontSize: 12 }}>
        変更した分だけ送ります（差分更新）。既に入っているものは消えません。
      </p>

      <div className="stack">
        <button
          type="button"
          onClick={() => setOpenPanel("instructions")}
          style={{ alignSelf: "flex-start" }}
        >
          instructions を編集
        </button>

        <Drawer
          open={openPanel === "instructions"}
          title="instructions を編集"
          onClose={() => setOpenPanel(null)}
        >
          <label htmlFor="bundle-instructions">instructions</label>
          <textarea
            id="bundle-instructions"
            className="grow"
            rows={4}
            placeholder="CLAUDE.md / AGENTS.md に配る内容"
            value={instructions}
            onChange={(e) => setInstructions(e.target.value)}
          />
          <button
            type="button"
            disabled={instructions.trim() === "" || saving}
            onClick={() => void send({ instructions })}
            style={{ alignSelf: "flex-start" }}
          >
            instructions を保存
          </button>
        </Drawer>
      </div>

      <div className="stack">
        <button
          type="button"
          onClick={() => {
            setSkillStep("list");
            setOpenPanel("skill");
          }}
          style={{ alignSelf: "flex-start" }}
        >
          Skill を編集
        </button>

        <Drawer
          open={openPanel === "skill"}
          title={skillStep === "list" ? "Skill" : editingSkill === null ? "Skill を作る" : `${editingSkill} を編集`}
          onClose={() => setOpenPanel(null)}
        >
          {skillStep === "list" ? (
            <ItemList
              kind="Skill"
              items={(current.data?.skills ?? []).map((skill) => ({
                key: skill.name,
                label: skill.name,
                onEdit: () => startEditSkill(skill),
                onRemove: () => send({ removeSkills: [skill.name] }),
              }))}
              onNew={startNewSkill}
              loading={current.loading}
              disabled={saving}
            />
          ) : (
          <>
          <button type="button" onClick={() => setSkillStep("list")} style={{ alignSelf: "flex-start" }}>
            ← 一覧へ
          </button>
          <div className="filters">
            <label htmlFor="bundle-skill-name">Skill 名</label>
            <input
              id="bundle-skill-name"
              placeholder="例: deploy"
              value={skillName}
              onChange={(e) => setSkillName(e.target.value)}
              style={{ width: 180 }}
            />
          </div>
          <label htmlFor="bundle-skill-body">SKILL.md</label>
          <textarea
            id="bundle-skill-body"
            className="grow"
            rows={4}
            placeholder="Skill の中身"
            value={skillBody}
            onChange={(e) => setSkillBody(e.target.value)}
          />
          <button
            type="button"
            className="primary"
            disabled={skillName.trim() === "" || skillBody.trim() === "" || saving}
            onClick={() =>
              void send({
                skills: [
                  {
                    name: skillName.trim(),
                    files: [
                      { path: "SKILL.md", content: skillBody },
                      // ★ 画面に出ていないファイルも一緒に送り直す。
                      // Skill は名前ごと差し替わるので、落とすと消える。
                      ...skillExtraFiles,
                    ],
                  },
                ],
                // 名前を変えたら、古い方は消す（作り直しではなく改名にする）。
                ...(editingSkill !== null && editingSkill !== skillName.trim()
                  ? { removeSkills: [editingSkill] }
                  : {}),
              })
            }
            style={{ alignSelf: "flex-start" }}
          >
            {editingSkill === null ? "Skill を追加" : "保存"}
          </button>
          {skillExtraFiles.length > 0 && (
            <p className="muted" style={{ fontSize: 12 }}>
              この Skill には SKILL.md 以外に {skillExtraFiles.length} 件のファイルがあります。
              そのまま保持されます。
            </p>
          )}
          </>
          )}
        </Drawer>
      </div>


      {/*
        ★ 中身の一覧は、それぞれのドロワーの中に置いた（一覧 → 編集）。
        ここに並べても「見えるだけで直せない」ので、選ぶ場所と同じにする。
        MCP だけはここに残す——1 ファイルで丸ごと差し替わるので、
        個別に選ぶ意味が無い。
      */}
      <div className="stack">
        <strong>MCP サーバ</strong>
        {current.loading && <Skeleton rows={2} height={14} />}
        {current.data != null && (
          <ul data-testid="bundle-contents">
            {Object.keys(current.data.mcpServers ?? {}).length === 0 && (
              <li className="muted">まだありません</li>
            )}
            {Object.keys(current.data.mcpServers ?? {}).map((name) => (
              <li key={`mcp-${name}`}>
                <span className="mono">{name}</span>{" "}
                {/* 値は伏せられて返る。触らない限り送り返さない。 */}
                <span className="muted">（秘密は伏せて表示）</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="stack">
        <button
          type="button"
          onClick={() => setAdaptersOpen(true)}
          style={{ alignSelf: "flex-start" }}
        >
          アダプタを編集
        </button>

        {/* ★ JSON は行数が多い。埋め込むと縦が足りないので、広い面で編集させる。 */}
        <Drawer
          open={adaptersOpen}
          title="アダプタを編集"
          onClose={() => setAdaptersOpen(false)}
          footer={
            <>
              <button
                type="button"
                className="primary"
                disabled={saving || parseJsonArrayOrNull(adaptersText) === null}
                onClick={() => {
                  const parsed = parseJsonArrayOrNull(adaptersText);
                  if (parsed !== null) void send({ adapters: parsed } as BundlePatch);
                }}
              >
                アダプタを保存
              </button>
              {parseJsonArrayOrNull(adaptersText) === null && (
                <span className="muted">JSON の配列として読めません</span>
              )}
            </>
          }
        >
          <p className="muted" style={{ margin: 0, fontSize: 12 }}>
            REST API を MCP の道具として見せます。鍵はここに書かず、
            <code>credentialId</code> で資格情報を参照してください
            （書くと登録時に弾かれます）。<code>baseUrl</code> は https のみ。
          </p>
          <Suspense fallback={<Skeleton rows={6} height={18} />}>
            <JsonEditor
              id="bundle-adapters"
              label="アダプタ（JSON）"
              fill
              value={adaptersText}
              onChange={setAdaptersText}
            />
          </Suspense>
        </Drawer>
      </div>

      <div className="stack">
        <button
          type="button"
          onClick={() => {
            setAgentStep("list");
            setOpenPanel("agent");
          }}
          style={{ alignSelf: "flex-start" }}
        >
          agent を編集
        </button>

        <Drawer
          open={openPanel === "agent"}
          title={
            agentStep === "list"
              ? "subagent"
              : editingAgent === null
                ? "subagent を作る"
                : `${editingAgent.name} を編集`
          }
          onClose={() => setOpenPanel(null)}
        >
          {agentStep === "list" ? (
            <ItemList
              kind="subagent"
              items={(current.data?.agents ?? []).map((agent) => ({
                key: `${agent.runner}/${agent.name}`,
                label: agent.name,
                hint: agent.runner,
                onEdit: () => startEditAgent(agent),
                onRemove: () =>
                  send({ removeAgents: [{ runner: agent.runner, name: agent.name }] }),
              }))}
              onNew={startNewAgent}
              loading={current.loading}
              disabled={saving}
            />
          ) : (
          <>
          <button type="button" onClick={() => setAgentStep("list")} style={{ alignSelf: "flex-start" }}>
            ← 一覧へ
          </button>
          <div className="filters">
            <label htmlFor="bundle-agent-runner">対象 Runner</label>
            <select
              id="bundle-agent-runner"
              value={agentRunner}
              onChange={(e) => setAgentRunner(e.target.value as "claude-code" | "codex")}
            >
              <option value="claude-code">claude-code</option>
              <option value="codex">codex</option>
            </select>
            <label htmlFor="bundle-agent-name">agent 名</label>
            <input
              id="bundle-agent-name"
              placeholder="例: extractor"
              value={agentName}
              onChange={(e) => setAgentName(e.target.value)}
              style={{ width: 180 }}
            />
          </div>
          <label htmlFor="bundle-agent-description">説明</label>
          <input
            id="bundle-agent-description"
            placeholder="いつ呼ぶかが分かる説明"
            value={agentDescription}
            onChange={(e) => setAgentDescription(e.target.value)}
          />
          <label htmlFor="bundle-agent-prompt">システムプロンプト</label>
          <textarea
            id="bundle-agent-prompt"
            rows={4}
            value={agentPrompt}
            onChange={(e) => setAgentPrompt(e.target.value)}
          />
          <div className="filters">
            <label htmlFor="bundle-agent-model">model</label>
            <input
              id="bundle-agent-model"
              placeholder="省略すると既定に従う"
              value={agentModel}
              onChange={(e) => setAgentModel(e.target.value)}
              style={{ width: 200 }}
            />
            {/* ★ Runner ごとに項目が違う。対応しない欄は出さない。
                出すと、入力してもドメインに弾かれる「保存できない欄」になる。 */}
            {agentRunner === "claude-code" ? (
              <>
                <label htmlFor="bundle-agent-tools">tools</label>
                <input
                  id="bundle-agent-tools"
                  placeholder="Read, Bash（カンマ区切り）"
                  value={agentTools}
                  onChange={(e) => setAgentTools(e.target.value)}
                  style={{ width: 200 }}
                />
              </>
            ) : (
              <>
                <label htmlFor="bundle-agent-sandbox">sandbox_mode</label>
                <select
                  id="bundle-agent-sandbox"
                  value={agentSandbox}
                  onChange={(e) => setAgentSandbox(e.target.value)}
                >
                  <option value="">（既定に従う）</option>
                  <option value="read-only">read-only</option>
                  <option value="workspace-write">workspace-write</option>
                  <option value="danger-full-access">danger-full-access</option>
                </select>
              </>
            )}
          </div>
          <button
            type="button"
            disabled={
              agentName.trim() === "" ||
              agentDescription.trim() === "" ||
              agentPrompt.trim() === "" ||
              saving
            }
            onClick={() => {
              const tools = agentTools
                .split(",")
                .map((t) => t.trim())
                .filter((t) => t !== "");
              void send({
                agents: [
                  {
                    runner: agentRunner,
                    name: agentName.trim(),
                    description: agentDescription.trim(),
                    prompt: agentPrompt,
                    ...(agentModel.trim() === "" ? {} : { model: agentModel.trim() }),
                    ...(agentRunner === "claude-code" && tools.length > 0 ? { tools } : {}),
                    ...(agentRunner === "codex" && agentSandbox !== ""
                      ? { sandboxMode: agentSandbox }
                      : {}),
                  },
                ],
                // ★ 名前や Runner を変えたら、古い方は消す。
                // 残すと、同じものが 2 つ配られることになる。
                ...(editingAgent !== null &&
                (editingAgent.name !== agentName.trim() || editingAgent.runner !== agentRunner)
                  ? { removeAgents: [editingAgent] }
                  : {}),
              });
            }}
            style={{ alignSelf: "flex-start" }}
            className="primary"
          >
            {editingAgent === null ? "agent を追加" : "保存"}
          </button>
          </>
          )}
        </Drawer>
      </div>

      <div className="stack">
        <button type="button" onClick={() => setMcpOpen(true)} style={{ alignSelf: "flex-start" }}>
          MCP を編集
        </button>

        <Drawer
          open={mcpOpen}
          title="MCP を編集"
          onClose={() => setMcpOpen(false)}
          footer={
            <>
              <button
                type="button"
                className="primary"
                disabled={saving || parseJsonOrNull(mcpText) === null}
                onClick={() => {
                  const parsed = parseJsonOrNull(mcpText);
                  if (parsed !== null) void send({ mcpServers: parsed });
                }}
              >
                MCP を保存
              </button>
              {parseJsonOrNull(mcpText) === null && (
                <span className="muted">JSON として読めません</span>
              )}
            </>
          }
        >
          <p className="muted" style={{ margin: 0, fontSize: 12 }}>
            1 ファイルで<strong>丸ごと差し替わります</strong>。ここに書いたものが全てになります。
            <code>env</code> の値は伏せ字（<code>null</code>）で表示されます。
            <strong>触ったところだけ書き換えてください</strong>——
            <code>null</code> のままなら前の値が保たれ、キーを消せばその環境変数も消えます。
          </p>
          <Suspense fallback={<Skeleton rows={6} height={18} />}>
            <JsonEditor
              id="bundle-mcp"
              label="mcpServers（JSON）"
              fill
              value={mcpText}
              onChange={setMcpText}
            />
          </Suspense>
        </Drawer>
      </div>

      <details>
        <summary className="muted">版の履歴</summary>
        <div className="table-wrap">
          <table>
            <tbody>
              {(versions.data?.items ?? []).map((version) => (
                <tr key={version.version}>
                  <td className="mono">{version.version.slice(0, 12)}</td>
                  <td className="mono">{JSON.stringify(version.manifest)}</td>
                  <td className="mono">{formatTimestamp(version.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
    </div>
  );
}
